import React, { memo, useReducer, useCallback, useMemo } from 'react';
import { FlatList } from 'react-native';
import { FormFixed } from '@redlink/components/organisms';
import {
  Space,
  Divider,
  Text,
  IconButton,
  Icon,
} from '@redlink/components/atoms';
import {
  TextInput,
  TagInput,
  ContactInfoItems,
  ProfileSection,
  FormSection,
  usePlan,
} from '@redlink/components/molecules';
import { ImageCircularUploader } from '@redlink/components/muscles';
import {
  ContactInfoEditPage,
  ResumeLinksEditPage,
} from '@redlink/components/pages';
import { createUseStyle, useTheme } from '@redlink/assets/styles';
import { useValues } from '@redlink/assets/values';
import { useLocale } from '@redlink/assets/locale';
import { useMutation, editProfileQuery } from '@redlink/services';
import { initialState, reducer, types, emptyAvatar } from './store';
import { freshCache } from './cache';

export const ProfileEditSection = memo(({ data }) => {
  const { dictionary } = useLocale();
  const { constants } = useValues();
  const { styles } = useStyle();
  const { showPlanDialog } = usePlan();
  const [
    {
      pageName,
      fullname,
      avatar: { id: avatarId, thumbnailUrl: avatarUrl },
      bio,
      currentTags,
      emailAddress,
      cellphoneNumber,
      address,
      currentContactInfo,
      editContactInfoIsVisible,
      currentLinks,
      editLinksIsVisible,
    },
    dispatch,
  ] = useReducer(reducer, initialState(data));

  const [editProfile, { loading: editProfileIsBusy }] = useMutation(
    editProfileQuery,
    {
      update: freshCache({ pageName }),
      limited: ({ subcode, minLength }) => {
        // const message = dictionary.limitation1(minLength);
        resetInputs();
        showPlanDialog();
      },
    }
  );

  const haveItemsChanged =
    data.pageName !== pageName ||
    data.fullname !== fullname ||
    data.bio !== bio ||
    data.emailAddress !== emailAddress ||
    data.cellphoneNumber !== cellphoneNumber ||
    data.address !== address ||
    data.avatarUrl !== avatarUrl ||
    data.tags.length !== currentTags.length ||
    !data.tags.every((tag) => currentTags.includes(tag)) ||
    data.links.length !== currentLinks.length ||
    !data.links.every(
      (link) => !!currentLinks.find((currentLink) => link.id === currentLink.id)
    );

  const renderLinks = useCallback((item) => {
    return <SocialLinkItem {...{ item }} />;
  }, []);

  const convertedLinks = useMemo(
    () => currentLinks.map((link) => link.id),
    [currentLinks]
  );

  const resetInputs = useCallback(() => {
    dispatch({
      type: types.SET_MULTI,
      payload: {
        pageName: data.pageName,
        bio: data.bio,
        emailAddress: data.emailAddress,
        fullname: data.fullname,
        cellphoneNumber: data.cellphoneNumber,
        address: data.address,
        avatar: { id: data.avatarId, thumbnailUrl: data.avatarUrl },
        currentTags: data.tags,
        currentLinks: data.links,
      },
    });
  }, [dispatch, data]);

  return (
    <FormFixed
      isBusy={editProfileIsBusy}
      actionsStyle={styles.actionsContainer}
      actions={[
        {
          variant: 'primary',
          title: dictionary.save,
          disabled: !haveItemsChanged,
          onPress: () =>
            editProfile({
              avatarAssetId: avatarId,
              fullname,
              bio,
              emailAddress,
              cellphoneNumber,
              address,
              tags: currentTags,
              pageName,
              links: convertedLinks,
            }),
        },
        {
          variant: 'secondary',
          title: dictionary.cancel,
          disabled: !haveItemsChanged,
          onPress: resetInputs,
        },
      ]}
    >
      <FormSection title={dictionary.profile}>
        <>
          <TextInput
            title={dictionary.yourLink}
            require
            en
            prefix={constants.pageLinkPrefix}
            value={pageName}
            onChangeText={(value) =>
              dispatch({
                type: types.SET_PAGE_NAME,
                payload: value.replace(' ', ''),
              })
            }
          />
          <Space size={8} />
          <Text style={styles.descriptionText}>
            {dictionary.shortLinkDescription}
          </Text>
        </>
        <Divider margin={10} />
        <ImageCircularUploader
          thumbnail={avatarUrl}
          value={avatarId}
          description={dictionary.avatarImageDescription}
          title={dictionary.avatarImage}
          onUpload={(result) =>
            dispatch({
              type: types.SET_AVATAR,
              payload: result || emptyAvatar,
            })
          }
        />
        <Divider margin={10} />
        <Text style={styles.titleText}>{dictionary.profileInfo}</Text>
        <Space size={8} />
        <TextInput
          require
          value={fullname}
          title={dictionary.titleNameOrCompany}
          onChangeText={(value) =>
            dispatch({ type: types.SET_FULLNAME, payload: value })
          }
        />
        <Space size={8} />
        <TextInput
          multiline="small"
          value={bio}
          title={dictionary.aboutMe}
          onChangeText={(value) =>
            dispatch({ type: types.SET_BIO, payload: value })
          }
        />
        <Divider margin={10} />
        <TagInput
          title={dictionary.tags}
          description={dictionary.tagDescription}
          boldTitle
          data={currentTags}
          onAdd={(item) =>
            dispatch({ type: types.SET_TAGS, payload: [...currentTags, item] })
          }
          onRemove={(item) =>
            dispatch({
              type: types.SET_TAGS,
              payload: [...currentTags.filter((p) => p !== item)],
            })
          }
        />
        <Divider margin={10} />
        <ProfileSection
          title={dictionary.contactInfo}
          description={dictionary.contactInfoProfileDescription}
          onPress={() =>
            dispatch({
              type: types.SET_MULTI,
              payload: {
                editContactInfoIsVisible: true,
                currentContactInfo: { emailAddress, cellphoneNumber, address },
              },
            })
          }
        >
          <ContactInfoItems
            data={{ emailAddress, cellphoneNumber, address }}
            panel
          />
        </ProfileSection>
        <Divider margin={10} />
        <FormSection containerStyle={styles.socialsContainer}>
          <ProfileSection
            title={dictionary.showSocialMedia}
            // description={dictionary.showSocialMediaDescription}
            onPress={() =>
              dispatch({
                type: types.SET_MULTI,
                payload: {
                  editLinksIsVisible: true,
                  currentLinks,
                },
              })
            }
          >
            <FlatList
              data={currentLinks}
              ItemSeparatorComponent={() => <Space size={8} />}
              renderItem={renderLinks}
              horizontal
            />
          </ProfileSection>
          <Space />
          <ResumeLinksEditPage
            onChange={({ selectedLinksIds, Links }) => {
              const selectedItems = Links.filter(
                (link) => selectedLinksIds.indexOf(link.id) !== -1
              );
              dispatch({
                type: types.SET_MULTI,
                payload: {
                  editLinksIsVisible: false,
                  currentLinks: selectedItems,
                },
              });
            }}
            {...{ pageName }}
            isVisible={editLinksIsVisible}
            {...(currentLinks && {
              currentLinks,
            })}
            onDismiss={() => {
              dispatch({
                type: types.SET_EDIT_LINKS_IS_VISIBLE,
                payload: false,
              });
            }}
          />
        </FormSection>
        <ContactInfoEditPage
          onChange={({
            cellphoneNumber: tempCellphoneNumber,
            emailAddress: tempEmailAddress,
            address: tempAddress,
          }) => {
            dispatch({
              type: types.SET_MULTI,
              payload: {
                editContactInfoIsVisible: false,
                emailAddress: tempEmailAddress,
                cellphoneNumber: tempCellphoneNumber,
                address: tempAddress,
              },
            });
          }}
          {...{ pageName }}
          isVisible={editContactInfoIsVisible}
          {...(currentContactInfo && {
            data: currentContactInfo,
          })}
          onDismiss={() => {
            dispatch({
              type: types.SET_EDIT_CONTACT_INFO_IS_VISIBLE,
              payload: false,
            });
          }}
        />
      </FormSection>
    </FormFixed>
  );
});

const SocialLinkItem = memo(
  ({
    item: {
      item: { logoName, shortLinkUrl },
    },
  }) => {
    const { colors } = useTheme();
    return (
      <IconButton onPress={() => window.open(shortLinkUrl, '_blank')}>
        <Icon color={colors.black} name={logoName} />
      </IconButton>
    );
  }
);

const useStyle = createUseStyle(({ colors }) => ({
  descriptionText: {
    fontSize: 12,
    color: colors.gray21,
    lineHeight: 15,
  },
  actionsContainer: {
    flexDirection: 'row-reverse',
  },
  socialsContainer: {
    paddingVertical: 0,
  },
  titleText: {
    color: colors.black,
    fontSize: 14,
  },
}));
